import React, { forwardRef } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Platform,
  TextInputProps,
} from 'react-native';

import { ChevronUp, ChevronDown } from './../icons';
import { formFieldInStack } from './formFieldStackContext';
import {
  ViewOnlyFieldValue,
  resolveIsViewOnlyPresentation,
} from './viewOnlyFieldUtils';
import { useFormPermissions } from '../../hooks/useFormPermissions';
import { colors } from '../../theme/colors';
import { spacing } from '../../theme/spacing';
import { fontSize, lineHeight, fontWeight } from '../../theme/typography';
import { webOutlineNone } from '../../theme/webOutlineNone';
import { ACCESSORY_VIEW_ID } from '../shared/KeyboardDismissToolbar';

export interface NumberInputProps
  extends Omit<TextInputProps, 'style' | 'onChangeText'> {
  label: string;
  value: string;
  onChangeText: (text: string) => void;
  placeholder?: string;
  required?: boolean;
  editable?: boolean;
  readOnlyMode?: boolean;
  ignorePermissions?: boolean;
  disabled?: boolean;
  error?: string;
  helperText?: string;
  containerStyle?: any;
  inputStyle?: any;
  style?: any;
  min?: number;
  max?: number;
  step?: number;
  showStepper?: boolean;
  /** When true, label is not shown (e.g. label + (i) from parent). */
  hideLabel?: boolean;
  /** Control inside the field, after the text. Unit chip uses this so its tap does not edit the number. */
  endAccessory?: React.ReactNode;
  /** Fires when editing ends, after the value is committed. */
  onCommit?: () => void;
  /**
   * When false (default), only digits and one optional decimal point are accepted (numeric entry).
   * Set true for values that may include units or labels (e.g. "208 V", "L-L 480").
   */
  acceptFreeText?: boolean;
}

export const NumberInput = forwardRef<TextInput, NumberInputProps>(
  (
    {
      label,
      value,
      onChangeText,
      placeholder,
      required = false,
      editable = true,
      readOnlyMode = false,
      ignorePermissions = false,
      disabled = false,
      error,
      helperText,
      containerStyle,
      inputStyle,
      style,
      min,
      max,
      step = 1,
      showStepper = true,
      hideLabel = false,
      endAccessory,
      onCommit,
      acceptFreeText = false,
      ...textInputProps
    },
    ref,
  ) => {
    const { keyboardType: keyboardTypeProp, ...restTextInputProps } =
      textInputProps;
    const { canEditRequest } = useFormPermissions();
    const hasError = Boolean(error);

    // Ensure value is always a string for controlled input
    // This is critical for iOS where the TextInput value prop must be a string
    const stringValue = value == null ? '' : String(value);

    // Determine if the input should be editable based on permissions
    const isEditable = ignorePermissions
      ? editable
      : editable && canEditRequest;
    const isPermissionLocked = !disabled && !readOnlyMode && !isEditable;
    const isDisabled = disabled || readOnlyMode || isPermissionLocked;
    const isReadOnly = readOnlyMode || editable === false || isPermissionLocked;
    const isViewOnlyPresentation = resolveIsViewOnlyPresentation({
      readOnlyMode,
      disabled,
      editable,
      canEditRequest,
      ignorePermissions,
    });
    const numericValue = parseFloat(stringValue) || 0;

    const handleIncrement = () => {
      if (isDisabled) return;
      const newValue = numericValue + step;
      if (max === undefined || newValue <= max) {
        onChangeText(newValue.toString());
      }
    };

    const handleDecrement = () => {
      if (isDisabled) return;
      const newValue = numericValue - step;
      if (min === undefined || newValue >= min) {
        onChangeText(newValue.toString());
      }
    };

    const handleTextChange = (text: string) => {
      if (acceptFreeText) {
        onChangeText(text);
        return;
      }
      // Allow empty string, numbers, and decimal points
      if (text === '' || /^\d*\.?\d*$/.test(text)) {
        onChangeText(text);
      }
    };

    // Handle iOS-specific issue where numeric keyboard doesn't trigger proper state updates
    const handleEndEditing = () => {
      // Ensure the value is properly synced when user finishes editing
      // This is particularly important on iOS where the numeric keyboard
      // doesn't have a "Done" button by default
      if (Platform.OS === 'ios') {
        onChangeText(stringValue);
      }
      onCommit?.();
    };

    if (isViewOnlyPresentation) {
      return (
        <ViewOnlyFieldValue
          label={label}
          value={stringValue}
          hideLabel={hideLabel}
          testID="number-input-container"
          valueTestID="view-only-field-value"
          containerStyle={[containerStyle, style, formFieldInStack]}
          error={error}
          helperText={helperText}
          trailing={endAccessory}
        />
      );
    }

    return (
      <View
        style={[styles.formGroup, containerStyle, style, formFieldInStack]}
        testID="number-input-container"
      >
        {!hideLabel ? (
          <View style={styles.labelContainer}>
            <Text style={styles.label}>
              {label}
              {required && <Text style={styles.required}> *</Text>}
            </Text>
          </View>
        ) : null}
        <View
          testID="number-input-surface"
          style={[
            styles.inputContainer,
            isDisabled && !isReadOnly && styles.disabledInputContainer,
            isReadOnly && styles.readOnlyInputContainer,
            hasError && styles.inputContainerError,
          ]}
        >
          <TextInput
            key={Platform.OS === 'ios' ? `input-${isDisabled}` : undefined}
            ref={ref}
            returnKeyType="done"
            inputAccessoryViewID={
              Platform.OS === 'ios' && !isDisabled
                ? ACCESSORY_VIEW_ID
                : undefined
            }
            style={[
              styles.input,
              styles.inputSingleLine,
              isDisabled && !isReadOnly && styles.disabledInput,
              isReadOnly && styles.readOnlyInput,
              isReadOnly &&
                Platform.OS === 'web' && {
                  WebkitTextFillColor: colors.textPrimary,
                },
              inputStyle,
            ]}
            value={stringValue}
            onChangeText={handleTextChange}
            onEndEditing={handleEndEditing}
            onBlur={handleEndEditing}
            placeholder={placeholder}
            editable={!isDisabled}
            keyboardType={
              acceptFreeText
                ? keyboardTypeProp ?? 'default'
                : keyboardTypeProp ?? 'numeric'
            }
            placeholderTextColor={
              disabled && !isReadOnly
                ? colors.textFormDisabled
                : colors.inputPlaceholder
            }
            accessibilityLabel={label}
            accessibilityHint={helperText || placeholder}
            {...Platform.select({
              web: {
                'aria-label': label,
                'aria-required': required,
                'aria-invalid': hasError,
                'aria-describedby': error
                  ? `${label}-error`
                  : helperText
                  ? `${label}-helper`
                  : undefined,
              },
            })}
            {...restTextInputProps}
          />
          {endAccessory}
          {showStepper && !isReadOnly && (
            <View style={styles.stepperContainer}>
              <TouchableOpacity
                style={[
                  styles.stepperButton,
                  isDisabled && styles.stepperButtonDisabled,
                  max !== undefined &&
                    numericValue >= max &&
                    styles.stepperButtonDisabled,
                ]}
                onPress={handleIncrement}
                disabled={
                  isDisabled || (max !== undefined && numericValue >= max)
                }
                accessibilityLabel={`Increment ${label}`}
                accessibilityRole="button"
              >
                <ChevronUp
                  size={14}
                  color={
                    isDisabled ? colors.borderMedium : colors.textSecondary
                  }
                />
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  styles.stepperButton,
                  isDisabled && styles.stepperButtonDisabled,
                  min !== undefined &&
                    numericValue <= min &&
                    styles.stepperButtonDisabled,
                ]}
                onPress={handleDecrement}
                disabled={
                  isDisabled || (min !== undefined && numericValue <= min)
                }
                accessibilityLabel={`Decrement ${label}`}
                accessibilityRole="button"
              >
                <ChevronDown
                  size={14}
                  color={
                    isDisabled ? colors.borderMedium : colors.textSecondary
                  }
                />
              </TouchableOpacity>
            </View>
          )}
        </View>
        {error && (
          <Text
            style={styles.errorText}
            {...Platform.select({
              web: { id: `${label}-error` },
            })}
          >
            {error}
          </Text>
        )}
        {helperText && !error && (
          <Text
            style={styles.helperText}
            {...Platform.select({
              web: { id: `${label}-helper` },
            })}
          >
            {helperText}
          </Text>
        )}
      </View>
    );
  },
);

NumberInput.displayName = 'NumberInput';

const styles = StyleSheet.create({
  formGroup: {
    marginBottom: 0,
  },
  /**
   * Block wrapper so label→control spacing is reliable on web (`Text` margins are not).
   * Matches `FormInput` `labelContainer` / `FormTwoOptionChoice` `labelRow`.
   */
  labelContainer: {
    width: '100%' as const,
    marginBottom: spacing.fieldVerticalGap,
  },
  label: {
    fontSize: fontSize.formLabel,
    lineHeight: lineHeight.formLabel,
    fontWeight: fontWeight.medium,
    color: colors.textPrimary,
  },
  required: {
    color: colors.error,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: spacing.formControlHeight,
    borderWidth: 1,
    borderColor: colors.inputBorderNeutral,
    borderRadius: spacing.formControlBorderRadius,
    backgroundColor: colors.backgroundForm,
    ...Platform.select({
      web: {
        boxSizing: 'border-box' as const,
        ':focus-within': {
          borderColor: colors.inputBorderFocus,
          boxShadow: colors.formControlFocusRing,
        },
        ':hover': {
          borderColor: colors.inputBorderInteractive,
        },
      },
    }),
  },
  inputContainerError: {
    borderColor: colors.error,
    ...Platform.select({
      web: {
        ':focus-within': {
          borderColor: colors.error,
          boxShadow: 'none',
        },
        ':hover': {
          borderColor: colors.error,
        },
      },
    }),
  },
  disabledInputContainer: {
    backgroundColor: colors.backgroundSecondary,
    borderColor: colors.inputBorderDisabled,
  },
  readOnlyInputContainer: {
    backgroundColor: colors.backgroundFormDisabled,
    borderColor: colors.inputBorderDisabled,
    ...Platform.select({
      web: {
        ':focus-within': {
          borderColor: colors.inputBorderDisabled,
          boxShadow: 'none',
        },
        ':hover': {
          borderColor: colors.inputBorderDisabled,
        },
      },
      default: {
        opacity: 1,
      },
    }),
  },
  input: {
    flex: 1,
    minWidth: 0,
    paddingHorizontal: spacing.formControlPaddingHorizontal,
    paddingVertical: spacing.formControlSingleLinePaddingVertical,
    fontSize: fontSize.formControl,
    lineHeight: lineHeight.formControl,
    color: colors.textPrimary,
    ...Platform.select({
      web: {
        ...webOutlineNone,
        '::placeholder': {
          color: colors.inputPlaceholder,
          opacity: 1,
        },
      },
    }),
  },
  /** Match {@link FormInput} single-line height so numeric fields align with text inputs and selects. */
  inputSingleLine: {
    height: spacing.formControlHeight,
    maxHeight: spacing.formControlHeight,
    ...Platform.select({
      web: {
        boxSizing: 'border-box' as const,
      },
      default: {},
    }),
  },
  stepperContainer: {
    flexDirection: 'column',
    alignSelf: 'stretch',
    justifyContent: 'center',
    borderLeftWidth: 1,
    borderLeftColor: colors.inputBorder,
  },
  stepperButton: {
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    ...Platform.select({
      web: {
        cursor: 'pointer',
        ':hover': {
          backgroundColor: colors.surfaceHover,
        },
      },
    }),
  },
  stepperButtonDisabled: {
    opacity: 0.5,
    ...Platform.select({
      web: {
        cursor: 'not-allowed',
        ':hover': {
          backgroundColor: 'transparent',
        },
      },
    }),
  },
  disabledInput: {
    backgroundColor: colors.backgroundSecondary,
    color: colors.textFormDisabled,
    ...Platform.select({
      web: {
        cursor: 'not-allowed' as const,
        '::placeholder': {
          color: colors.textFormDisabled,
          opacity: 1,
        },
      },
    }),
  },
  readOnlyInput: {
    color: colors.textPrimary,
    ...Platform.select({
      web: {
        cursor: 'default' as const,
        '::placeholder': {
          color: colors.inputPlaceholder,
          opacity: 1,
        },
      },
      default: {
        opacity: 1,
      },
    }),
  },
  errorText: {
    fontSize: fontSize.charCount,
    color: colors.error,
    marginTop: spacing.xs,
  },
  helperText: {
    fontSize: fontSize.charCount,
    color: colors.textSecondary,
    marginTop: spacing.xs,
  },
});
